import { useState } from 'react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { RankedBars } from '@/shared/ui/charts'
import { fmtNumber, fmtPercent } from '@/shared/lib/format'
import type { GeographyCell, GeographyResponse } from '@/shared/api/types'
import { analyticsGeography } from '../api'
import { MeasuredWith, MixedCurrencyNote, Money, NotRun, Pct, Pp, UnattributedNote, Value } from '../parts'
import { AnalyticsScreen } from './shell'

/**
 * The portfolio by place.
 *
 * ## Why this is not a choropleth
 *
 * Two reasons, and the second is the real one.
 *
 * The endpoint returns no geometry — it sends unit ids and figures, so a map
 * would mean fetching every boundary in scope to render one screen.
 *
 * More importantly, a choropleth shades AREA by RATE, and area has no
 * relationship to the population underneath it. Kenya's largest wards by area
 * are its emptiest; a coverage map of Kenya is a map of Kenya's land, coloured.
 * Every reader takes away "the north is doing well" from a picture in which the
 * north is simply big. A ranked grid says the same thing without the visual
 * argument nobody meant to make.
 *
 * The map that IS worth drawing — one unit's boundary, on its own record — is
 * `UnitMap`, and it lives on the geography feature where a unit is the subject.
 */
export function ProgramGeographyScreen() {
  return (
    <AnalyticsScreen
      title="Geography"
      description="Coverage, cost and measured change by unit."
      load={(f) => analyticsGeography(f)}
      isEmpty={(d) => (d.cells?.length ?? 0) === 0}
      exportKind="GEOGRAPHY"
      showUnitType
    >
      {(d) => <GeographyBody d={d} />}
    </AnalyticsScreen>
  )
}

type MetricKey = 'coverage' | 'enrolledLinked' | 'costPerBeneficiary' | 'meanUplift' | 'delivered'

const METRICS: { key: MetricKey; label: string; hint: string }[] = [
  { key: 'coverage', label: 'Coverage', hint: 'Enrolled ÷ eligible' },
  { key: 'enrolledLinked', label: 'Enrolled', hint: 'Beneficiaries linked to a voter record' },
  { key: 'delivered', label: 'Deliveries', hint: 'Delivery events recorded' },
  { key: 'costPerBeneficiary', label: 'Cost each', hint: 'Attributed spend ÷ beneficiaries' },
  { key: 'meanUplift', label: 'Mean uplift', hint: 'Percentage points, where measured' },
]

function GeographyBody({ d }: { d: GeographyResponse }) {
  const [metric, setMetric] = useState<MetricKey>('coverage')
  const cells = d.cells ?? []
  const active = METRICS.find((m) => m.key === metric)!

  const totalEnrolled = cells.reduce((n, c) => n + c.enrolledLinked, 0)
  const totalMeasured = cells.reduce((n, c) => n + c.measured, 0)
  const totalMeasurable = cells.reduce((n, c) => n + c.measurable, 0)
  const withCoverage = cells.filter((c) => c.coverage != null)
  // `currencyMixedReason` is PER CELL — it means "this one unit spans currencies".
  // It is null on a purely-USD ward and null on a purely-KES ward, so testing it
  // alone said "not mixed" about a set that mixed at the table level. The set is
  // mixed when the cells do not agree, whatever any single cell says.
  const currencies = new Set(cells.map((c) => c.currency).filter(Boolean) as string[])
  const cellMixed = cells.find((c) => c.currencyMixedReason)?.currencyMixedReason ?? null
  const mixed = currencies.size > 1
    ? `These units are recorded in ${currencies.size} currencies (${[...currencies].sort().join(', ')}).`
    : cellMixed
  const currency = currencies.size === 1 ? [...currencies][0]! : null

  const ranked = cells
    .map((c) => ({ cell: c, value: numeric(c, metric) }))
    .filter((r): r is { cell: GeographyCell; value: number } => r.value != null)
    .sort((a, b) => b.value - a.value)
    .slice(0, 15)
    .map((r) => ({
      label: r.cell.unitName,
      value: r.value,
      hint: r.cell.unitPath ?? undefined,
    }))

  const format = metric === 'coverage'
    ? (n: number) => fmtPercent(n, 0)
    : metric === 'meanUplift'
      ? (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)} pp`
      : fmtNumber

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Units" value={fmtNumber(cells.length)}
          sub={`at ${(d.unitTypeCode ?? '').toLowerCase().replace(/_/g, ' ') || 'unit'} level`} />
        <MetricCard label="Enrolled" value={fmtNumber(totalEnrolled)}
          sub="Linked to a voter record, so placeable" />
        <MetricCard label="Units with a denominator" value={fmtNumber(withCoverage.length)}
          sub={`${fmtNumber(cells.length - withCoverage.length)} have no eligible pool`} />
        <MetricCard
          label="Measured"
          value={<MeasuredWith measured={totalMeasured} measurable={totalMeasurable} />}
          sub="Uplift figures below rest on this fraction"
        />
      </KpiStrip>

      <MixedCurrencyNote reason={mixed} what="The cost column" />
      {/* Summed only when every cell agrees on a currency. Adding a dollar cell
          to a shilling cell and stamping the first cell's currency on the result
          is the exact arithmetic the banner above is warning about. */}
      {!mixed && (
        <UnattributedNote
          amount={cells.reduce((n, c) => n + Number(c.spendUnattributed ?? 0), 0)}
          currency={currency}
        />
      )}

      <Panel
        title={`Top units by ${active.label.toLowerCase()}`}
        description={active.hint}
        right={(
          <select className="erp-input h-8 text-erp-xs" aria-label="Metric"
            value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)}>
            {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        )}
      >
        {/* A cost ranked by BAR LENGTH across currencies is the same error the
            money screen refuses to draw. The other metrics are counts and
            fractions and are comparable whatever the currency. */}
        {metric === 'costPerBeneficiary' && mixed
          ? <NotRun what="This ranking" reason={mixed} />
          : (
            <RankedBars points={ranked} formatValue={format}
              emptyMessage={`No unit in scope has a ${active.label.toLowerCase()} figure.`} />
          )}
        {ranked.length < cells.length && (
          <p className="mt-2 text-erp-xs text-muted-foreground">
            Showing the top {ranked.length} of {fmtNumber(cells.length)} units. Units with no
            figure for this metric are not ranked — they are in the table below with the reason.
          </p>
        )}
      </Panel>

      <Panel title="All units" bodyClassName="p-0">
        <ErpTable head={(
          <>
            <Th>Unit</Th>
            <Th className="text-right">Enrolled</Th>
            <Th className="text-right">Eligible</Th>
            <Th className="text-right">Coverage</Th>
            <Th className="text-right">Deliveries</Th>
            <Th className="text-right">Cost each</Th>
            <Th className="text-right">Mean uplift</Th>
            <Th>Measured</Th>
          </>
        )}>
          {cells.length === 0
            ? <EmptyRow colSpan={8} message="No unit is in scope under this filter." />
            : [...cells].sort((a, b) => b.enrolledLinked - a.enrolledLinked).map((c) => (
              <tr key={c.unitId}>
                <Td title={c.unitPath ?? undefined}>{c.unitName}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(c.enrolledLinked)}</Td>
                <Td className="text-right tabular-nums"><Value value={c.eligible} shortReason /></Td>
                <Td className="text-right"><Pct value={c.coverage} shortReason /></Td>
                <Td className="text-right tabular-nums">{fmtNumber(c.delivered)}</Td>
                <Td className="text-right">
                  <Money value={c.costPerBeneficiary} currency={c.currency} mixedReason={c.currencyMixedReason} />
                </Td>
                <Td className="text-right">
                  <Pp value={c.meanUplift} reason="Nobody in this unit has a post-enrolment reading." shortReason />
                </Td>
                <Td><MeasuredWith measured={c.measured} measurable={c.measurable} /></Td>
              </tr>
            ))}
        </ErpTable>
      </Panel>
    </div>
  )
}

function numeric(c: GeographyCell, key: MetricKey): number | null {
  const raw = c[key]
  if (raw == null) return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}
